/**
 * Переиспользуемый дропдаун подсказок пользователей при вводе MXID.
 *
 * Почему ОДИН компонент, а не два разных под invite и DM: Антон 12.09
 * сказал «при любом поиске», значит следующее поле ввода подключается
 * одной строкой. Два компонента означали бы два места для каждой правки.
 *
 * ИСТОЧНИК ДАННЫХ — проп `suggestions`, уже готовый массив из spaceUsers.ts.
 * Компонент не знает про Matrix: он получает список и фильтрует его
 * по подстроке. Это осознанное разделение: UI отвечает за показ,
 * matrix-слой — за то, КОГО показывать.
 *
 * ФИЛЬТРАЦИЯ: по ДВУМ полям сразу — экранное имя И логин (локальная часть
 * MXID). Регистр не важен. Подстрока, не только начало: «ван» находит
 * и «Иванов», и `@ivanov:...`.
 *
 * КЛАВИАТУРА: стрелки ходят по списку, Enter выбирает, Escape закрывает
 * дропдаун (но НЕ диалог — первый Escape гасит список, второй окно).
 * МЫШЬ: клик по строке выбирает. Выбранное подставляет полный MXID в поле.
 *
 * Совпадений нет — список НЕ показывается. Никаких «ничего не найдено»:
 * человек может вводить внешний MXID целиком, и обвинять его в этот момент
 * в ошибке нельзя (Антон 12.09).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

import { color, font, radius, space } from "./theme";
import type { UserSuggestion } from "../matrix/spaceUsers";

/** Максимум строк в дропдауне; дальше — прокрутка. Больше — шторка в пол-экрана. */
const MAX_VISIBLE = 8;

/**
 * Фильтр подсказок по введённому тексту.
 *
 * Вынесен из компонента, потому что правило «что считается совпадением»
 * нетривиально (два поля, ведущая собака, регистр) и должно жить рядом
 * с типом, а не внутри JSX.
 */
export function filterSuggestions(
  suggestions: UserSuggestion[],
  query: string,
): UserSuggestion[] {
  // Поле начинается с «@» (начальное значение): фильтр не должен
  // спотыкаться о ведущую собаку.
  const q = query.replace(/^@/, "").toLowerCase();
  if (!q) return suggestions;
  return suggestions.filter((s) => {
    const name = s.displayName.toLowerCase();
    const login = s.userId.replace(/^@/, "").split(":")[0].toLowerCase();
    return name.includes(q) || login.includes(q);
  });
}

export function UserSuggest({
  suggestions,
  query,
  onSelect,
  onDismiss,
}: {
  /** Уже отфильтрованный список подсказок (после filterSuggestions). */
  suggestions: UserSuggestion[];
  /** Текущий текст в поле (нужен, чтобы понять, показывать ли дропдаун). */
  query: string;
  /** Выбран MXID — подставить в поле и закрыть список. */
  onSelect: (userId: string) => void;
  /** Закрыть дропдаун без выбора (Escape, потеря фокуса). */
  onDismiss: () => void;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  // При смене запроса сбрасываем выделение на первую строку: иначе
  // стрелка вниз попадала бы на элемент, которого уже нет в списке.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Прокрутка к выделенной строке: без неё клавиатурная навигация
  // теряла бы элемент за пределами видимости.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const item = list.children[activeIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  // Клавиатура: слушаем на document, потому что фокус остаётся в input,
  // а стрелки/Enter должны работать для списка.
  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (suggestions.length === 0) return;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation(); // иначе Field.onEnter/стрелки в input сработают тоже
          setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex((i) => Math.max(i - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          e.stopPropagation(); // не даём Field.onEnter отправить форму
          onSelect(suggestions[activeIndex].userId);
          break;
        case "Escape":
          // Первый Escape гасит дропдаун, второй закроет диалог
          // (обработчик диалога на window сработает после нашего stopPropagation).
          e.stopPropagation();
          onDismiss();
          break;
      }
    },
    [suggestions, activeIndex, onSelect, onDismiss],
  );

  useEffect(() => {
    document.addEventListener("keydown", onKey, true); // capture: перехват до диалога
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onKey]);

  if (suggestions.length === 0) return null;

  return (
    <div style={styles.dropdown} data-testid="user-suggest" ref={listRef}>
      {suggestions.slice(0, MAX_VISIBLE).map((s, i) => (
        <div
          key={s.userId}
          style={{
            ...styles.item,
            ...(i === activeIndex ? styles.itemActive : {}),
          }}
          data-testid="user-suggest-item"
          data-user-id={s.userId}
          onMouseDown={(e) => {
            // mousedown, а не click: blur поля срабатывает раньше click,
            // и без preventDefault список закрывался бы до обработки выбора.
            e.preventDefault();
            onSelect(s.userId);
          }}
          onMouseEnter={() => setActiveIndex(i)}
        >
          <div style={styles.name}>{s.displayName}</div>
          {/* MXID обязателен в строке: экранные имена повторяются, и два
              «Андрей» без MXID неразличимы (Антон 12.09). */}
          <div style={styles.mxid}>{s.userId}</div>
        </div>
      ))}
    </div>
  );
}

/** Стили — только токены из theme.ts, никаких литеральных цветов. */
const styles: Record<string, CSSProperties> = {
  dropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    marginTop: space.xs,
    background: color.bg,
    border: `1px solid ${color.border}`,
    borderRadius: radius.sm,
    maxHeight: MAX_VISIBLE * 52, // ~52px на строку: имя + MXID
    overflowY: "auto",
    zIndex: 70, // выше футера диалога
  },
  item: {
    display: "flex",
    alignItems: "baseline",
    gap: space.md,
    padding: `${space.sm}px ${space.lg}px`,
    cursor: "pointer",
    fontSize: font.small,
    color: color.text,
  },
  itemActive: {
    background: color.hover,
  },
  name: {
    fontWeight: font.weightBold,
    flexShrink: 0,
  },
  mxid: {
    color: color.textDim,
    fontSize: font.micro,
    // MXID может быть длинным; обрезаем, а не переносим — строка
    // должна оставаться однострочной для сканирования.
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};
